import CheckIcon from "@mui/icons-material/Check";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { skipToken } from "@reduxjs/toolkit/query";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { useEffect, useMemo, useRef, useState } from "react";

import LoadingButton from "_shared/_views/loading/loading-button";
import PlaceAutocomplete from "_shared/addresses/_views/place-autocomplete";
import Address from "_shared/addresses/address";
import Guid from "_shared/guids/guid";
import { hasEntityAgreementChanges } from "legal-entities/agreements/_api/commands/update-entity-agreement-api-command";
import {
  getEntityAgreementApiErrorMessage,
  InvalidEntityAgreementError,
  isEntityAgreementRequestCanceled,
  isOnboardingWorkTemplateChangedError,
  useAcceptEntityAgreementMutation,
  useGetEntityAgreementOnboardingTemplateQuery,
  useGetEntityAgreementByIdQuery,
  useGetOnboardingWorkTemplateQuery,
  useUpdateEntityAgreementMutation,
} from "legal-entities/agreements/_api/entity-agreement-api-service";
import EntityAgreement from "legal-entities/agreements/entity-agreement";
import EntityNameInput from "legal-entities/entity-names/_views/inputs/entity-name-input";
import EntityName from "legal-entities/entity-names/entity-name";
import OfficerOnboardingProgress from "legal-entities/onboarding/_views/officer-onboarding-progress";
import WhoHasAccessButton from "permissions/_views/records/who-has-access-button";
import { useWhoHasAccess } from "permissions/_views/records/who-has-access";
import { useNavigate, useParams } from "react-router";
import type Session from "users/__session/session";
import { useSession } from "users/__session/session-context";
import OnboardingTemplateTerms from "work/onboarding-templates/_views/onboarding-template-terms";

const PageContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  height: "100%",
  overflow: "auto",
  padding: theme.spacing(4, 2, 6),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2, 1, 4),
  },
}));
const PageContent = styled(Box)(() => ({
  margin: "0 auto",
  maxWidth: 840,
}));
const Title = styled("h2")(({ theme }) => ({
  ...theme.typography.h4,
  margin: 0,
}));
// The title keeps its line while Who has access fits beside it, and the button wraps beneath it on a phone.
const TitleRow = styled(Box)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1, 2),
  justifyContent: "space-between",
  marginBottom: theme.spacing(1),
}));
const SectionCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));
const LegalEntityLocationInput = styled(PlaceAutocomplete)(({ theme }) => ({
  width: "100%",
  maxWidth: 520,
  marginTop: theme.spacing(2),
}));
const OrganizationNameInput = styled(EntityNameInput)(({ theme }) => ({
  width: "100%",
  maxWidth: 520,
  marginTop: theme.spacing(2),
}));
const AcceptButton = styled(LoadingButton)(({ theme }) => ({
  marginTop: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
}));
const ServiceTermsLoadError = styled(Alert)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));
const ServiceTermsLoadingMessage = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));
const AgreementConsent = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));
export default function EntityAgreementPage() {
  const session = useSession();
  const params = useParams<{ id: string }>();

  const agreementId = useMemo(() => (params.id ? Guid.fromJSON(params.id) ?? undefined : undefined), [params.id]);
  const sessionAgreement = useMemo(
    () => session.openEntityAgreements.find((agreement: EntityAgreement) => agreement.id.value === params.id),
    [params.id, session.openEntityAgreements],
  );
  const agreementQuery = useGetEntityAgreementByIdQuery(
    !sessionAgreement && agreementId ? { id: agreementId } : skipToken,
  );

  useEffect(() => {
    if (!params.id) {
      console.error(new Error("No agreement ID provided"));
      enqueueSnackbar("Entity agreement is currently unavailable", { variant: "error" });
    }
  }, [params.id]);

  useEffect(() => {
    if (!agreementQuery.isError) return;
    if (isEntityAgreementRequestCanceled(agreementQuery.error)) return;
    console.error(agreementQuery.error);
    enqueueSnackbar("Entity agreement is currently unavailable", { variant: "error" });
  }, [agreementQuery.error, agreementQuery.isError]);

  const originalAgreement = sessionAgreement ?? agreementQuery.data;
  if (!originalAgreement) return null;

  return (
    <EntityAgreementForm key={originalAgreement.id.value} originalAgreement={originalAgreement} session={session} />
  );
}

function EntityAgreementForm({
  originalAgreement,
  session,
}: {
  originalAgreement: EntityAgreement;
  session: Session;
}) {
  const [formEntityName, setFormEntityName] = useState<EntityName | undefined>(originalAgreement.entityName);
  const [formLocation, setFormLocation] = useState<Address | undefined>(originalAgreement.entityLocation);
  const [saving, setSaving] = useState<boolean>(false);
  const navigate = useNavigate();
  const locationServicesRef = useRef<boolean>(false);
  const [updateEntityAgreement] = useUpdateEntityAgreementMutation();
  const [acceptEntityAgreement] = useAcceptEntityAgreementMutation();
  const agreementAccess = useWhoHasAccess("organization-agreements", "the organization agreement");
  const isLegacyAgreement = (originalAgreement.workflowVersion ?? 1) <= 1;
  const linkedTemplateQuery = useGetEntityAgreementOnboardingTemplateQuery({ id: originalAgreement.id });
  const onboardingTemplateQuery = useGetOnboardingWorkTemplateQuery(isLegacyAgreement ? undefined : skipToken);
  const displayedTemplate = linkedTemplateQuery.data ?? (isLegacyAgreement ? onboardingTemplateQuery.data : undefined);

  useEffect(() => {
    if (!isLegacyAgreement || !onboardingTemplateQuery.isError) return;
    if (isEntityAgreementRequestCanceled(onboardingTemplateQuery.error)) return;

    console.error(onboardingTemplateQuery.error);
    enqueueSnackbar("AttorneyHub service terms are currently unavailable. Please try again.", { variant: "error" });
  }, [isLegacyAgreement, onboardingTemplateQuery.error, onboardingTemplateQuery.isError]);

  useEffect(() => {
    if (isLegacyAgreement || !linkedTemplateQuery.isError) return;
    if (isEntityAgreementRequestCanceled(linkedTemplateQuery.error)) return;

    console.error(linkedTemplateQuery.error);
    enqueueSnackbar("The agreement terms are currently unavailable. Please try again.", { variant: "error" });
  }, [isLegacyAgreement, linkedTemplateQuery.error, linkedTemplateQuery.isError]);

  async function handleAcceptAgreement() {
    setSaving(true);

    try {
      if (!originalAgreement) throw new Error("Original agreement is missing");
      if (!validEntityAgreement) throw new InvalidEntityAgreementError("Agreement is invalid");
      // An officer who changes neither the name nor the location has nothing to save, and the
      // endpoint refuses an empty patch document rather than treating it as a no-op. Sending one
      // failed the accept before it started, which is the whole default onboarding path.
      const finalAgreement = hasEntityAgreementChanges(originalAgreement, validEntityAgreement)
        ? await updateEntityAgreement({
            oldAgreement: originalAgreement,
            updatedAgreement: validEntityAgreement,
          }).unwrap()
        : validEntityAgreement;

      const onboardingWorkTemplateVersion = displayedTemplate?.version;
      if (!onboardingWorkTemplateVersion) throw new Error("AttorneyHub service terms are unavailable");

      const authResponse = await acceptEntityAgreement({
        entityAgreement: finalAgreement,
        onboardingWorkTemplateVersion,
        onboardingWorkTemplateId: displayedTemplate?.id,
      }).unwrap();

      session.initializeFromAuthResponse(authResponse);

      const onboardingWork = authResponse.user.onboardingWork;
      const currentProposalId = onboardingWork?.currentProposalId;
      navigate(
        onboardingWork?.state === "ProposalInProgress" && currentProposalId
          ? `/proposals/active/revise/${currentProposalId}/details`
          : "/proposals",
        { replace: true },
      );
    } catch (error: unknown) {
      if (isOnboardingWorkTemplateChangedError(error)) {
        await (isLegacyAgreement ? onboardingTemplateQuery.refetch() : linkedTemplateQuery.refetch());
        enqueueSnackbar("AttorneyHub service terms changed. Review the updated terms and accept again.", {
          variant: "info",
        });
        return;
      }

      if (isEntityAgreementRequestCanceled(error)) {
        navigate("/dashboard");
        return;
      }

      console.error(error);

      enqueueSnackbar(getEntityAgreementApiErrorMessage(error, "Couldn't accept agreement. Please try again"), {
        variant: "error",
      });
    } finally {
      setSaving(false);
    }
  }

  const validEntityAgreement = (() => {
    if (!formEntityName || !formLocation) {
      return undefined;
    }
    try {
      return new EntityAgreement({
        id: originalAgreement.id,
        entityLocation: formLocation,
        entityName: formEntityName,
        termsFileId: originalAgreement.termsFileId,
        onboardingTemplateId: originalAgreement.onboardingTemplateId,
        onboardingTemplateVersion: originalAgreement.onboardingTemplateVersion,
        workflowVersion: originalAgreement.workflowVersion,
        acceptedTemplateSnapshot: originalAgreement.acceptedTemplateSnapshot,
      });
    } catch {
      return undefined;
    }
  })();

  if (!formEntityName) return null;

  return (
    <PageContainer>
      <PageContent>
        <OfficerOnboardingProgress activeStep={1} organizationName={formEntityName.value} />
        <Stack spacing={3}>
          <Box>
            <TitleRow>
              <Title>
                Activate {formEntityName.value}
              </Title>
              {agreementAccess.mayOpen && <WhoHasAccessButton onClick={() => agreementAccess.open(originalAgreement.id)} />}
            </TitleRow>
            {agreementAccess.drawer}
            <Typography color="text.secondary">
              Confirm the organization details and review the agreements that start your AttorneyHub workspace.
            </Typography>
          </Box>

          <SectionCard role="region" aria-labelledby="organization-details-heading" elevation={1}>
            <Typography id="organization-details-heading" component="h3" variant="h6">
              Confirm organization details
            </Typography>
            <Typography color="text.secondary" variant="body2">
              These details identify the organization you are authorized to activate.
            </Typography>
            <OrganizationNameInput
              label="Organization Name"
              initialValue={formEntityName}
              variant="outlined"
              onChange={(updatedEntityName?: EntityName) => {
                setFormEntityName(updatedEntityName);
              }}
            />
            <LegalEntityLocationInput
              label="Organization Location"
              variant="outlined"
              required
              value={formLocation ?? null}
              locationServicesDisabledRef={locationServicesRef}
              onLocationServicesDisabledChange={(newValue) => {
                locationServicesRef.current = newValue;
              }}
              onChange={(address: Address | null) => {
                setFormLocation(address ?? undefined);
              }}
            />
          </SectionCard>

          <SectionCard role="region" aria-labelledby="agreements-heading" elevation={1}>
            <Typography id="agreements-heading" component="h3" variant="h6">
              Review Proposed Work Agreement between {formEntityName.value} and {displayedTemplate?.vendorDisplayName ?? "AttorneyHub"}.
            </Typography>
            <Typography color="text.secondary" variant="body2">
              Activating your organization {formEntityName.value} accepts the entity work agreement and starts the services below.
            </Typography>

            {displayedTemplate ? (
              <OnboardingTemplateTerms terms={displayedTemplate} />
            ) : (isLegacyAgreement ? onboardingTemplateQuery.isError : linkedTemplateQuery.isError) ? (
              <ServiceTermsLoadError
                severity="error"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => void (isLegacyAgreement ? onboardingTemplateQuery.refetch() : linkedTemplateQuery.refetch())}
                  >
                    Try Again
                  </Button>
                }
              >
                AttorneyHub service terms are currently unavailable. You cannot finish setup until they load.
              </ServiceTermsLoadError>
            ) : (
              <ServiceTermsLoadingMessage aria-live="polite" color="text.secondary">
                Loading agreement terms...
              </ServiceTermsLoadingMessage>
            )}

            <AgreementConsent color="text.secondary" variant="body2">
              By selecting the button below, you accept both agreements and open the proposal workspace for
              {formEntityName.value}. You will perform the approval and hire actions.
            </AgreementConsent>
            <AcceptButton
              id="entity-agreement-submit-button"
              variant="contained"
              color="primary"
              loading={saving}
              disabled={saving || !validEntityAgreement || !displayedTemplate}
              startIcon={<CheckIcon />}
              onClick={handleAcceptAgreement}
            >
              Accept Agreements and Finish Setup
            </AcceptButton>
          </SectionCard>
        </Stack>
      </PageContent>
    </PageContainer>
  );
}
